// Firebase initialization for the Checkover admin web interface.
// Reads configuration from Vite env vars (see .env.example).
// Connects to the same Firebase project as the iOS app (picup-7676f).

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore, type Firestore, type FirestoreSettings } from 'firebase/firestore';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';

/**
 * Local emulator mode — `npm run dev:emulators` (see README). Development only:
 * `import.meta.env.DEV` is false in a production build, so every emulator branch
 * below is dead code there and cannot connect a deployed bundle to localhost.
 * App Check is skipped because the emulators do not enforce it; that is also why
 * this is the only way to sign in locally, as reCAPTCHA v3 issues no token on
 * 127.0.0.1 and every Firestore read would be denied.
 */
const useEmulators = import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === '1';
const EMULATOR_HOST = '127.0.0.1';

const requiredEnv = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

const missing = requiredEnv.filter((key) => !import.meta.env[key]);
if (missing.length > 0) {
  // eslint-disable-next-line no-console
  console.error(
    `[firebase] Missing required env vars: ${missing.join(', ')}. ` +
      `Copy .env.example to .env.local and fill in values from the Firebase console.`
  );
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

const appCheckSiteKey = useEmulators ? undefined : import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY;
export const appCheck: AppCheck | null = appCheckSiteKey
  ? initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;

if (!appCheckSiteKey && !useEmulators) {
  // eslint-disable-next-line no-console
  console.warn(
    '[firebase] VITE_FIREBASE_APPCHECK_SITE_KEY is not set. ' +
      'Callable functions with App Check enforcement, including lookupVehicle, will fail.'
  );
}

export const auth: Auth = getAuth(app);
// Firebase 10.14 supports useFetchStreams at runtime. Disable buffered fetch
// streams and use short long-polls for Safari and restrictive network proxies.
const firestoreSettings: FirestoreSettings & { useFetchStreams: boolean } = {
  experimentalForceLongPolling: true,
  experimentalLongPollingOptions: { timeoutSeconds: 5 },
  useFetchStreams: false,
};
export const db: Firestore = initializeFirestore(app, firestoreSettings);

// PR-26 §7/D2: evidence lives in the europe-west2 bucket, co-located with the
// Firestore DB and callables. The default bucket was US-CENTRAL1, so evidence
// bytes were crossing the Atlantic. Overridable via env for other environments.
export const evidenceBucketUrl =
  import.meta.env.VITE_FIREBASE_EVIDENCE_BUCKET || 'gs://picup-7676f-evidence-eu';
export const storage: FirebaseStorage = getStorage(app, evidenceBucketUrl);
storage.maxOperationRetryTime = 15_000;

// Cloud Functions co-located in europe-west2 (PR-26 §7).
export const functionsRegion =
  import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || 'europe-west2';
export const functions: Functions = getFunctions(app, functionsRegion);

if (useEmulators) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, EMULATOR_HOST, 8080);
  connectStorageEmulator(storage, EMULATOR_HOST, 9199);
  connectFunctionsEmulator(functions, EMULATOR_HOST, 5001);
  // eslint-disable-next-line no-console
  console.info('[firebase] Local emulator mode: no production data is reachable from this tab.');
}
